import Cookies from 'js-cookie';
import type { IArticleConfig } from '~/config/article';
import { ENV } from '~/config/article/env';
import { debugLog } from '~/debug';
import type { IIframeInterface } from '~/modules/iframes/Iframe';
import Session from '~/modules/Session';
import TokenHandler from '~/modules/TokenHandler';
import { getColorLuminance, getRealBackgroundColor } from '~/utils/color';
import { safeLocalStorageGet, safeLocalStorageSetDelete, safeLocalStorageSetForDuration } from '~/utils/localStorage';
import { Listener } from './Listener';
import type { PowerbarListener } from './PowerbarListener';
import { makeRequest } from '~/utils/general';
import CommunicationCreator from '~/modules/CommunicationCreator/index';

export class EmotesListener extends Listener {
  private communicationCreator: CommunicationCreator;
  constructor(
    protected widgetObject: IIframeInterface,
    private readonly articleConfig: IArticleConfig,
    private readonly powerbarListeners: PowerbarListener[] = []
  ) {
    super(widgetObject);
    window.addEventListener('message', this.listener);
    this.postMessage({ canUseCookies: Session.canUseCookies });
    if (this.articleConfig.amp) {
      if (this.articleConfig.emotes.hideRecommendedArticles) {
        this.postMessage({ disableRecommendations: true });
      }

      if (articleConfig.theme?.darkMode) {
        this.postMessage({ enableDarkMode: true });
      }
    }
    this.postMessage({ loginToken: TokenHandler.getToken() });
    this.postMessage({ votedEmote: this.getVotedEmote() });
    let themeChangeAttempts = 50;
    /** Send the background color of the element that contains our widget so that it can decide whether it's switching to dark mode or light mode */
    const widgetThemeInterval = setInterval(() => {
      if (themeChangeAttempts === 0) {
        clearInterval(widgetThemeInterval);
        return;
      }
      const widgetContainer = widgetObject.iframe.parentElement;
      if (widgetContainer) {
        const bgColor = getRealBackgroundColor(widgetContainer);
        this.postMessage({ backgroundColor: bgColor });
        this.postMessage({ shouldUseDarkMode: getColorLuminance(widgetContainer) < 0.3 });
        clearInterval(widgetThemeInterval);
      }
      themeChangeAttempts--;
    }, 100);
  }

  /**
   * Emotes message listener
   * NOTE: We need to use arrow function here to bind function to this object instead of the event.
   * @param {any} event - event received from emotes widget
   */
  protected listener = (event: any) => {
    if (!this.isValidEvent(event)) {
      return;
    }
    debugLog('log', '[Vuukle.js | Emotes] message:', event);

    if (event.data.syncWidget instanceof Object) {
      /**
       * Emotes needs to be synced only with powerbar since only powerbar shows most voted emote
       * So we resend this message to powerbars only
       */
      this.powerbarListeners.forEach((powerbarListener) =>
        powerbarListener.postMessage({ syncWidget: event.data.syncWidget })
      );
    }

    if (event.data.getVotedEmote) {
      this.postMessage({ votedEmote: this.getVotedEmote() || null });
    }

    if (event.data.deleteVotedEmote) {
      this.deleteEmote();
    }

    if (event.data.emoteVote) {
      this.setEmote(event.data.emoteVote);
    }

    if (event.data.noArticles && !this.articleConfig.amp) {
      const emotesParent = this.widgetObject.iframe.parentElement;
      if (emotesParent) {
        this.communicationCreator.createQuizInstanceNearElement(emotesParent);
      }
    }
  };

  public updateVotedEmote() {
    this.postMessage({ votedEmote: this.getVotedEmote() || null });
  }

  private deleteEmoteRequest = async (cookieId: string) => {
    return makeRequest(
      'DELETE',
      `${ENV.API_URL}/api/v1/emote_ratings/remove`,
      {
        apiKey: this.articleConfig.apiKey,
        articleAvatar: this.articleConfig.img,
        articleId: this.articleConfig.articleId,
        cookieId,
        host: this.articleConfig.host,
        tag: this.articleConfig.tags,
        title: this.articleConfig.title,
        uri: this.articleConfig.url,
      },
      'application/json',
      TokenHandler.getToken(),
      true
    );
  };

  private postEmoteVote = async (emote: 1 | 2 | 3 | 4 | 5 | 6) => {
    return makeRequest(
      'POST',
      `${ENV.API_URL}/api/v1/emote_ratings`,
      {
        amp: this.articleConfig.amp,
        apiKey: this.articleConfig.apiKey,
        articleAvatar: this.articleConfig.img,
        articleId: this.articleConfig.articleId,
        emote,
        host: this.articleConfig.host,
        tag: this.articleConfig.tags,
        title: this.articleConfig.title,
        uri: this.articleConfig.url,
      },
      'application/json',
      TokenHandler.getToken(),
      true
    );
  };

  /**
   * @name getVotedEmote
   * @description Get voted emote from the cookie to show that user already voted
   * @returns {number | undefined} - If emote found returns emote number if not then `undefined`
   */
  private getVotedEmote = (): 1 | 2 | 3 | 4 | 5 | 6 | undefined => {
    const votedEmoteNumb = this.articleConfig.amp
      ? parseInt(safeLocalStorageGet(this.getVotedCookieName()) || '')
      : parseInt(Cookies.get(this.getVotedCookieName()) || '', 10);
    if (votedEmoteNumb >= 1 && votedEmoteNumb <= 6) {
      return votedEmoteNumb as 1 | 2 | 3 | 4 | 5 | 6;
    }
    return;
  };

  /**
   * Save Emote vote into cookies to read later and post voted emote to the server
   * @param {number} emoteNumber - emote number to save from 1 to 6
   * @returns {Promise} emotes APIs post response
   */
  private setEmote = async (emoteNumber: 1 | 2 | 3 | 4 | 5 | 6): Promise<any> => {
    // Save voted emote to cookies so we will know that user voted
    const options: Cookies.CookieAttributes = { expires: 60, sameSite: 'Lax' };
    let res = await this.postEmoteVote(emoteNumber);
    if (res.success == false && res.errors.includes('invalid_apikey')) {
      console.warn(
        '[VUUKLE] Emote post request was not completed because the apiKey is either invalid or not given. Please check your configuration.'
      );
    }
    if (Session.canUseCookies) {
      if (this.articleConfig.amp) {
        import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet(this.getVotedCookieName(), () => {
            safeLocalStorageSetForDuration(this.getVotedCookieName(), emoteNumber.toString(), 60 * 24);
          });
          consentManager.default.executeWhenStorageValueCanBeSet(this.getCookieId(), () => {
            safeLocalStorageSetForDuration(this.getCookieId(), res.data.cookieId, 60 * 24);
          });
        });
      } else {
        import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
          consentManager.default.executeWhenStorageValueCanBeSet(this.getVotedCookieName(), () => {
            Cookies.set(this.getVotedCookieName(), emoteNumber.toString(), options);
          });
          consentManager.default.executeWhenStorageValueCanBeSet(this.getCookieId(), () => {
            Cookies.set(this.getCookieId(), res.data.cookieId, options);
          });
        });
      }
    } else {
      Session.addStorageUpdateCallback(() => {
        if (this.articleConfig.amp) {
          import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
            consentManager.default.executeWhenStorageValueCanBeSet(this.getVotedCookieName(), () => {
              safeLocalStorageSetForDuration(this.getVotedCookieName(), emoteNumber.toString(), 60 * 24);
            });
            consentManager.default.executeWhenStorageValueCanBeSet(this.getCookieId(), () => {
              safeLocalStorageSetForDuration(this.getCookieId(), res.data.cookieId, 60 * 24);
            });
          });
        } else {
          import(/* webpackChunkName: "cmg" */ '../../ConsentManager').then((consentManager) => {
            consentManager.default.executeWhenStorageValueCanBeSet(this.getVotedCookieName(), () => {
              Cookies.set(this.getVotedCookieName(), emoteNumber.toString(), options);
            });
            consentManager.default.executeWhenStorageValueCanBeSet(this.getCookieId(), () => {
              Cookies.set(this.getCookieId(), res.data.cookieId, options);
            });
          });
        }
      });
    }
    return res;
  };

  private deleteEmote = (): Promise<any> => {
    const cookieId = this.articleConfig.amp
      ? safeLocalStorageGet(this.getCookieId()) || ''
      : Cookies.get(this.getCookieId()) || '';
    return this.deleteEmoteRequest(cookieId);
  };

  /**
   * Remove all the emote cookies
   */
  public removeCookies = () => {
    const cookieIdOptions: Cookies.CookieAttributes = { expires: 30, sameSite: 'Lax' };
    const emoteVoteCookieOptions: Cookies.CookieAttributes = { expires: 60, sameSite: 'Lax' };
    if (this.articleConfig.amp) {
      safeLocalStorageSetDelete(this.getCookieId(), null);
      safeLocalStorageSetDelete(this.getVotedCookieName(), null);
      try {
        const keyList = Object.keys(localStorage);
        if (keyList) {
          keyList.forEach((key) => {
            // If it's an emote vote
            if (key.includes('vuukle_emotes_vote')) {
              safeLocalStorageSetDelete(key, null);
            }
          });
        }
      } catch (_e) {}
    } else {
      Cookies.remove(this.getCookieId(), cookieIdOptions);
      Cookies.remove(this.getVotedCookieName(), emoteVoteCookieOptions);
      const otherVotes = Cookies.get();
      const cookieList = Object.keys(otherVotes);
      if (cookieList) {
        cookieList.forEach((cookie) => {
          // If it's an emote vote
          if (cookie.includes('vuukle_emotes_vote')) {
            Cookies.remove(cookie, emoteVoteCookieOptions);
          }
        });
      }
    }
  };

  /** Get emotes vote cookieId cookie name */
  private getCookieId = () => {
    return Session.userAPIKey && !Session.userAPIKey.startsWith('$')
      ? `vuukle_emotes_vote_${this.articleConfig.host}&${this.articleConfig.articleId}&CookieId&${Session.userAPIKey}`
      : `vuukle_emotes_vote_${this.articleConfig.host}&${this.articleConfig.articleId}&CookieId`;
  };

  /** Get emotes voted cookie name */
  private getVotedCookieName = () => {
    return Session.userAPIKey && !Session.userAPIKey.startsWith('$')
      ? `vuukle_emotes_vote_${this.articleConfig.host}&${this.articleConfig.articleId}&${Session.userAPIKey}`
      : `vuukle_emotes_vote_${this.articleConfig.host}&${this.articleConfig.articleId}`;
  };

  public setCommunicationCreatorInstance(commCreator: CommunicationCreator) {
    this.communicationCreator = commCreator;
  }

  /**
   * Change widget theme
   * @param theme Light or dark mode
   */
  public changeTheme(theme: 'light' | 'dark') {
    this.postMessage({ changeTheme: theme });
  }
}
